import { useCallback, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../../context/AuthContext";
import { formatDate } from "../../Utils/Constant";
import BookPosition from "../RiskManagement/BookPosition";
import useSportAnalytics, { asList, sameId } from "./useSportAnalytics";
import { FancyMarket, OddsMarket } from "./Markets";
import PremiumSport from "./PremiumSport";
import { MatchBetsDialog, RecentBets } from "./MatchBets";
import MatchMedia from "./MatchMedia";
import "./SportAnalytics.css";

// Remount on either route parameter so filters, pending requests and live feeds
// cannot carry data from another event/market into this page.
export default function SportAnalytics() {
  const { eventId, marketId } = useParams();
  return (
    <MatchDetails
      key={`${eventId}:${marketId}`}
      eventId={eventId}
      marketId={marketId}
    />
  );
}

function MatchDetails({ eventId, marketId }) {
  const userId = useContextSelector(
    AuthContext,
    (context) => context.user?._id,
  );
  const {
    detail,
    markets,
    odds,
    bookmakerOdds,
    fancy,
    premium,
    togglePremium,
    groups,
    positions,
    toss,
    refresh,
    loading,
    errors,
  } = useSportAnalytics(eventId, marketId, userId);
  const [dialog, setDialog] = useState("");
  const [book, setBook] = useState(null);
  const closeDialog = useCallback(() => setDialog(""), []);
  const setting = (type) =>
    asList(detail?.matchSetting).find((item) => item.type === type);
  const isCricket =
    detail?.gameType === "cricket" || String(detail?.eventType) === "4";
  const tossEnabled =
    isCricket &&
    detail?.isTossEnable &&
    detail?.eventDateTime &&
    Date.now() < new Date(detail.eventDateTime).getTime() - 40 * 60000;
  const bookmaker =
    bookmakerOdds.find((market) => sameId(market.eventId, eventId)) ||
    bookmakerOdds[0];

  return (
    <div className="main_wrap risk-inner-scope sport-analytics">
      <div className="total_all analytics-title">
        <div>
          <h2>{detail?.eventName || "Match Details"}</h2>
          {detail && (
            <p>
              {detail.seriesName}
              {detail.eventDateTime
                ? ` | ${formatDate(detail.eventDateTime)}`
                : ""}
              {detail.status === "in_play" && (
                <strong className="green"> | In-Play</strong>
              )}
            </p>
          )}
        </div>
        <div className="analytics-actions">
          <Link className="btn" to="/risk-management/riskManagement">
            Risk Management
          </Link>
          <button type="button" className="btn-send" onClick={refresh}>
            Refresh
          </button>
        </div>
      </div>
      {errors.length > 0 && (
        <div className="analytics-errors" role="alert">
          {errors.map((error) => (
            <p key={error}>{error}</p>
          ))}
        </div>
      )}
      {loading && (
        <p className="analytics-empty" role="status">
          Loading match details…
        </p>
      )}
      {detail && (
        <div className="analytics-grid">
          <div className="analytics-markets">
            <MatchMedia detail={detail} />
            {detail.matchOdds === "on" &&
              markets.map(
                (market) =>
                  asList(market.jsonData).length > 0 && (
                    <OddsMarket
                      key={market.marketId}
                      title={market.market}
                      marketId={market.marketId}
                      runners={asList(market.jsonData)}
                      odds={odds.find(
                        (item) =>
                          sameId(item.mi, market.marketId) &&
                          (!item.eventId || sameId(item.eventId, eventId)),
                      )}
                      positions={positions.betFair}
                      limits={setting("betFaire")}
                    />
                  ),
              )}
            {isCricket &&
              detail.hasBookmaker &&
              detail.bookMaker === "on" &&
              asList(detail.jsonData).length > 0 && (
                <OddsMarket
                  title="Bookmaker"
                  marketId={marketId}
                  runners={asList(detail.jsonData)}
                  odds={bookmaker}
                  positions={positions.bookmaker}
                  limits={setting("bookmaker")}
                  bookmaker
                />
              )}
            {/* {tossEnabled && asList(toss?.rt).length > 0 && (
              <OddsMarket
                title="Toss"
                runners={asList(detail.jsonData).filter((runner) =>
                  asList(toss.rt).some((rate) =>
                    sameId(rate.ri, runner.SelectionId),
                  ),
                )}
                odds={toss}
                limits={setting("betFaire")}
                toss
              />
            )} */}
            {isCricket && detail.fancy === "on" && fancy.length > 0 && (
              <FancyMarket
                data={fancy}
                positions={positions.fancyAndPremium}
                limits={setting("fancy")}
                onBook={(item) =>
                  setBook({
                    ...item,
                    eventId,
                    fancyName: item.fancyName || item.name,
                  })
                }
              />
            )}
            {premium.length > 0 && (
              <PremiumSport
                data={premium}
                handelPremiumFancy={togglePremium}
              />
            )}
            {detail.matchOdds !== "on" &&
              detail.bookMaker !== "on" &&
              detail.fancy !== "on" &&
              premium.length === 0 && (
                <p className="analytics-empty">
                  No active markets for this match.
                </p>
              )}
          </div>
          <RecentBets
            groups={groups}
            onAllBets={() => setDialog("all")}
            onProfitLoss={() => setDialog("settled")}
          />
        </div>
      )}
      {dialog && (
        <MatchBetsDialog
          mode={dialog}
          groups={groups}
          eventId={eventId}
          detail={detail}
          onClose={closeDialog}
        />
      )}
      {book && (
        <BookPosition
          isOpen
          object={book}
          onClose={() => setBook(null)}
          runPosition
        />
      )}
    </div>
  );
}
